import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiGet, apiPost } from "../../shared/apiClient";
import { ImageUploadField } from "../../shared/ImageUploadField";
import { SearchableSelectField } from "../../shared/SearchableSelectField";
import { IconList, IconRefresh, IconSave } from "../../shared/toolbarIcons";

const emptyForm = () => ({
  name: "",
  email: "",
  phoneNo: "",
  dob: "",
  photo: "",
  gender: "",
  isEmailVerified: false,
  isPhoneNoVerifies: false,
  lastLogin: "",
  whatsAppNo: ""
});

const emptyAddressForm = () => ({
  address: "",
  addressType: "",
  countryId: "",
  stateId: "",
  cityId: "",
  pobox: "",
  contactPersonName: "",
  contactPersonMob: "",
  isDefault: false
});

function toDateInput(value) {
  if (!value) return "";
  if (typeof value === "string" && value.length >= 10) return value.slice(0, 10);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function toDateTimeInput(value) {
  if (!value) return "";
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function toIsoOrNull(value) {
  if (!value) return null;
  return new Date(value).toISOString();
}

function mapAddressDtoToForm(item) {
  return {
    address: item.address ?? "",
    addressType: item.addressType ?? "",
    countryId: item.countryId ?? "",
    stateId: item.stateId ?? "",
    cityId: item.cityId ?? "",
    pobox: item.pobox ?? "",
    contactPersonName: item.contactPersonName ?? "",
    contactPersonMob: item.contactPersonMob ?? "",
    isDefault: Boolean(item.isDefault)
  };
}

function pickPrimaryAddress(list) {
  if (!list?.length) return null;
  return list.find((a) => a.isDefault) ?? list[0];
}

function mapCustomerToForm(c) {
  return {
    name: c.name ?? "",
    email: c.email ?? "",
    phoneNo: c.phoneNo ?? "",
    dob: toDateInput(c.dob),
    photo: c.photo ?? "",
    gender: c.gender ?? "",
    isEmailVerified: Boolean(c.isEmailVerified),
    isPhoneNoVerifies: Boolean(c.isPhoneNoVerifies),
    lastLogin: toDateTimeInput(c.lastLogin),
    whatsAppNo: c.whatsAppNo ?? ""
  };
}

function addressFormHasIntent(af, addressEditingId) {
  if (addressEditingId) return true;
  return Boolean(
    af.address.trim() ||
      af.countryId ||
      af.stateId ||
      af.cityId ||
      af.addressType ||
      af.pobox ||
      af.contactPersonName ||
      af.contactPersonMob ||
      af.isDefault
  );
}

export function CustomersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const editParam = searchParams.get("edit");
  const addressEditParam = searchParams.get("addressEdit");
  /** After "New address", skip one hydrate so we do not immediately reload the default row from the server. */
  const skipAddressHydrateRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyForm());

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [addressEditingId, setAddressEditingId] = useState(null);
  const [addressForm, setAddressForm] = useState(() => emptyAddressForm());
  const [addressError, setAddressError] = useState(null);

  const activeTab = searchParams.get("tab") === "addresses" ? "addresses" : "customer";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [coRes, sRes, ciRes] = await Promise.all([
        apiGet("/api/Countries"),
        apiGet("/api/States"),
        apiGet("/api/Cities")
      ]);
      if (cancelled) return;
      if (coRes.ok) setCountries(coRes.data ?? []);
      if (sRes.ok) setStates(sRes.data ?? []);
      if (ciRes.ok) setCities(ciRes.data ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!editParam && searchParams.get("addressEdit")) {
      const next = new URLSearchParams(searchParams);
      next.delete("addressEdit");
      setSearchParams(next, { replace: true });
    }
  }, [editParam, searchParams, setSearchParams]);

  useEffect(() => {
    if (!editParam) {
      setEditingId(null);
      setForm(emptyForm());
      setLoading(false);
      setAddressEditingId(null);
      setAddressForm(emptyAddressForm());
      return;
    }
    const id = Number(editParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const res = await apiGet(`/api/Customers/${id}`);
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load customer");
        return;
      }
      const c = res.data;
      setEditingId(c.id);
      setForm(mapCustomerToForm(c));
      const keepAddressDraft = new URLSearchParams(window.location.search).get("addressEdit");
      if (!keepAddressDraft) {
        skipAddressHydrateRef.current = false;
        setAddressEditingId(null);
        setAddressForm(emptyAddressForm());
      }
    })();
    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [editParam]);

  useEffect(() => {
    if (!addressEditParam || activeTab !== "addresses" || !editingId) {
      return;
    }
    const id = Number(addressEditParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    (async () => {
      const res = await apiGet(`/api/CustomerAddresses/${id}`);
      if (cancelled) return;
      if (!res.ok) {
        setAddressError(res.error ?? "Failed to load address");
        return;
      }
      const item = res.data;
      if (item.customerId !== editingId) {
        setAddressError("Address does not belong to this customer.");
        return;
      }
      setAddressEditingId(item.id);
      setAddressForm(mapAddressDtoToForm(item));
    })();
    return () => {
      cancelled = true;
    };
  }, [addressEditParam, activeTab, editingId]);

  useEffect(() => {
    if (!editingId || activeTab !== "addresses" || addressEditParam) {
      return;
    }
    if (skipAddressHydrateRef.current) {
      skipAddressHydrateRef.current = false;
      return;
    }
    let cancelled = false;
    (async () => {
      const res = await apiGet(`/api/CustomerAddresses/by-customer/${editingId}`);
      if (cancelled) return;
      if (!res.ok) {
        setAddressError(res.error ?? "Failed to load addresses");
        return;
      }
      const list = Array.isArray(res.data) ? res.data : [];
      const pick = pickPrimaryAddress(list);
      if (!pick) {
        setAddressEditingId(null);
        setAddressForm(emptyAddressForm());
        return;
      }
      setAddressEditingId(pick.id);
      setAddressForm(mapAddressDtoToForm(pick));
    })();
    return () => {
      cancelled = true;
    };
  }, [editingId, activeTab, addressEditParam]);

  const selectTab = (tab) => {
    if (tab === "customer") {
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      next.delete("addressEdit");
      setSearchParams(next, { replace: true });
      setAddressError(null);
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("tab", "addresses");
    next.delete("addressEdit");
    setSearchParams(next, { replace: true });
    setAddressError(null);
  };

  const saveAll = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      setError("Name and email are required.");
      return;
    }

    const addrIntent = addressFormHasIntent(addressForm, addressEditingId);
    if (addrIntent) {
      if (!addressForm.address.trim() || !addressForm.countryId || !addressForm.stateId || !addressForm.cityId) {
        setError(null);
        setAddressError("Fill address, country, state, and city—or clear the address fields.");
        return;
      }
    }

    setSaving(true);
    setError(null);
    setAddressError(null);

    const now = new Date().toISOString();
    const priorId = editingId;

    const custRes = await apiPost("/api/Customers", {
      id: priorId ?? 0,
      name: form.name,
      email: form.email,
      phoneNo: form.phoneNo || null,
      dob: toIsoOrNull(form.dob),
      photo: form.photo || null,
      gender: form.gender || null,
      isEmailVerified: form.isEmailVerified,
      isPhoneNoVerifies: form.isPhoneNoVerifies,
      lastLogin: toIsoOrNull(form.lastLogin),
      createdDate: now,
      updatedDate: priorId ? now : null,
      whatsAppNo: form.whatsAppNo || null
    });

    if (!custRes.ok) {
      setError(custRes.error ?? "Failed to save customer");
      setSaving(false);
      return;
    }

    const customerId = priorId ?? custRes.data?.id;
    if (!customerId) {
      setError("Customer was saved but no id was returned.");
      setSaving(false);
      return;
    }

    if (addrIntent) {
      const addrRes = await apiPost("/api/CustomerAddresses", {
        id: addressEditingId ?? 0,
        customerId,
        address: addressForm.address,
        addressType: addressForm.addressType || null,
        countryId: Number(addressForm.countryId),
        stateId: Number(addressForm.stateId),
        cityId: Number(addressForm.cityId),
        pobox: addressForm.pobox || null,
        contactPersonName: addressForm.contactPersonName || null,
        contactPersonMob: addressForm.contactPersonMob || null,
        isDefault: addressForm.isDefault
      });
      if (!addrRes.ok) {
        setAddressError(addrRes.error ?? "Failed to save address");
        setSaving(false);
        return;
      }
    }

    setSaving(false);
    navigate("/customers-list");
  };

  const onRefreshCustomer = async () => {
    setError(null);
    if (editParam) {
      const id = Number(editParam);
      if (!Number.isFinite(id)) return;
      setLoading(true);
      const res = await apiGet(`/api/Customers/${id}`);
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load customer");
        return;
      }
      const c = res.data;
      setEditingId(c.id);
      setForm(mapCustomerToForm(c));
    } else {
      setEditingId(null);
      setForm(emptyForm());
    }
  };

  const onRefreshClick = async () => {
    if (activeTab === "addresses") {
      setAddressError(null);
      if (!editingId) return;
      const aid = searchParams.get("addressEdit");
      if (aid && editingId) {
        const res = await apiGet(`/api/CustomerAddresses/${Number(aid)}`);
        if (res.ok && res.data.customerId === editingId) {
          const item = res.data;
          setAddressEditingId(item.id);
          setAddressForm(mapAddressDtoToForm(item));
        }
      } else {
        const res = await apiGet(`/api/CustomerAddresses/by-customer/${editingId}`);
        if (!res.ok) {
          setAddressError(res.error ?? "Failed to load addresses");
          return;
        }
        const list = Array.isArray(res.data) ? res.data : [];
        const pick = pickPrimaryAddress(list);
        if (!pick) {
          setAddressEditingId(null);
          setAddressForm(emptyAddressForm());
        } else {
          setAddressEditingId(pick.id);
          setAddressForm(mapAddressDtoToForm(pick));
        }
      }
    } else {
      await onRefreshCustomer();
    }
  };

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>{editingId ? "Edit Customer" : "Add Customer"}</h2>
        </div>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void saveAll()}
            disabled={saving || loading}
            title="Save customer and address"
            aria-label="Save"
          >
            <IconSave />
          </button>
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void onRefreshClick()}
            disabled={saving || loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <IconRefresh />
          </button>
          <Link to="/customers-list" className="icon-button icon-button--toolbar" title="Customers list" aria-label="Open list">
            <IconList />
          </Link>
        </div>
      </div>

      <div className="card-body">
        <div className="form-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            className={`form-tab ${activeTab === "customer" ? "form-tab--active" : ""}`}
            aria-selected={activeTab === "customer"}
            onClick={() => selectTab("customer")}
          >
            Customer
          </button>
          <button
            type="button"
            role="tab"
            className={`form-tab ${activeTab === "addresses" ? "form-tab--active" : ""}`}
            aria-selected={activeTab === "addresses"}
            onClick={() => selectTab("addresses")}
          >
            Addresses
          </button>
        </div>

        {activeTab === "customer" && (
          <form
            id="customers-form"
            className="form form-fields-bottomline"
            onSubmit={(e) => {
              e.preventDefault();
              void saveAll();
            }}
          >
            <div className="form-row form-row--4">
              <label className="field">
                <span>Name</span>
                <input type="text" value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} />
              </label>
              <label className="field">
                <span>Email</span>
                <input type="email" value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} />
              </label>
              <label className="field">
                <span>Phone No</span>
                <input type="text" value={form.phoneNo} onChange={(e) => setForm((prev) => ({ ...prev, phoneNo: e.target.value }))} />
              </label>
              <label className="field">
                <span>WhatsApp No</span>
                <input type="text" value={form.whatsAppNo} onChange={(e) => setForm((prev) => ({ ...prev, whatsAppNo: e.target.value }))} />
              </label>
            </div>
            <div className="form-row form-row--4">
              <label className="field">
                <span>Date of Birth</span>
                <input type="date" value={form.dob} onChange={(e) => setForm((prev) => ({ ...prev, dob: e.target.value }))} />
              </label>
              <label className="field">
                <span>Gender</span>
                <input type="text" value={form.gender} onChange={(e) => setForm((prev) => ({ ...prev, gender: e.target.value }))} />
              </label>
              <ImageUploadField label="Photo" value={form.photo} onChange={(photo) => setForm((prev) => ({ ...prev, photo }))} />
            </div>
            {error && <div className="error-banner">{error}</div>}
            {addressError && activeTab === "customer" && <div className="error-banner">{addressError}</div>}
          </form>
        )}

        {activeTab === "addresses" && (
          <div className="form-tab-panel">
            {error && <div className="error-banner">{error}</div>}
            {addressError && <div className="error-banner">{addressError}</div>}

            <form
              id="customer-address-inline-form"
              className="form form-fields-bottomline"
              onSubmit={(e) => {
                e.preventDefault();
                void saveAll();
              }}
            >
              <label className="field">
                <span>Address</span>
                <input value={addressForm.address} onChange={(e) => setAddressForm((p) => ({ ...p, address: e.target.value }))} />
              </label>
              <label className="field">
                <span>Address Type</span>
                <input value={addressForm.addressType} onChange={(e) => setAddressForm((p) => ({ ...p, addressType: e.target.value }))} />
              </label>
              <SearchableSelectField
                label="Country"
                options={countries}
                value={addressForm.countryId}
                onChange={(nextValue) =>
                  setAddressForm((p) => ({
                    ...p,
                    countryId: nextValue === "" ? "" : Number(nextValue),
                    stateId: "",
                    cityId: ""
                  }))
                }
                getOptionLabel={(option) => option.name || `Country #${option.id}`}
                getSearchText={(option) => `${option.name ?? ""} ${option.code ?? ""} ${option.id ?? ""}`}
                placeholder="Search country"
              />
              <SearchableSelectField
                label="State"
                options={states.filter((s) => !addressForm.countryId || s.countryId === Number(addressForm.countryId))}
                value={addressForm.stateId}
                onChange={(nextValue) =>
                  setAddressForm((p) => ({
                    ...p,
                    stateId: nextValue === "" ? "" : Number(nextValue),
                    cityId: ""
                  }))
                }
                getOptionLabel={(option) => option.name || `State #${option.id}`}
                getSearchText={(option) => `${option.name ?? ""} ${option.code ?? ""} ${option.id ?? ""}`}
                placeholder="Search state"
              />
              <SearchableSelectField
                label="City"
                options={cities.filter((ci) => !addressForm.stateId || ci.stateId === Number(addressForm.stateId))}
                value={addressForm.cityId}
                onChange={(nextValue) =>
                  setAddressForm((p) => ({
                    ...p,
                    cityId: nextValue === "" ? "" : Number(nextValue)
                  }))
                }
                getOptionLabel={(option) => option.name || `City #${option.id}`}
                getSearchText={(option) => `${option.name ?? ""} ${option.id ?? ""}`}
                placeholder="Search city"
              />
              <label className="field">
                <span>POBOX</span>
                <input value={addressForm.pobox} onChange={(e) => setAddressForm((p) => ({ ...p, pobox: e.target.value }))} />
              </label>
              <label className="field">
                <span>Contact Person Name</span>
                <input value={addressForm.contactPersonName} onChange={(e) => setAddressForm((p) => ({ ...p, contactPersonName: e.target.value }))} />
              </label>
              <label className="field">
                <span>Contact Person Mobile</span>
                <input value={addressForm.contactPersonMob} onChange={(e) => setAddressForm((p) => ({ ...p, contactPersonMob: e.target.value }))} />
              </label>
              {(addressEditingId || searchParams.get("addressEdit")) && (
                <div className="form-actions">
                  <button
                    className="btn btn-ghost"
                    type="button"
                    onClick={() => {
                      skipAddressHydrateRef.current = true;
                      setAddressEditingId(null);
                      setAddressForm(emptyAddressForm());
                      const next = new URLSearchParams(searchParams);
                      next.delete("addressEdit");
                      setSearchParams(next, { replace: true });
                    }}
                  >
                    New address
                  </button>
                </div>
              )}
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
